// src/features/alerts/PushNotifications.jsx
// Push notification UI: permission prompt, emergency modal, notification history.
// All push logic lives in usePushNotifications() hook.
// This file: JSX only.

import { usePushNotifications, PUSH_STATUS } from '@hooks/usePushNotifications';
import { T } from '@components/theme';
import { BottomSheet } from '@components/ui';

// ── Permission prompt (shown at app level once) ────────────────────
export function PushPermissionPrompt({ onDismiss }) {
  const { status, requestPermission } = usePushNotifications();

  if (status !== PUSH_STATUS.DEFAULT) return null;

  return (
    <div style={{ margin: '8px 14px 0', padding: '12px 14px', borderRadius: 12,
                  background: T.amberLight, border: `1px solid ${T.amber}40` }}>
      <div style={{ fontWeight: 800, fontSize: 13, color: T.amber, marginBottom: 4 }}>
        🔔 Enable emergency alerts
      </div>
      <div style={{ fontSize: 11, color: T.dim, lineHeight: 1.5, marginBottom: 10 }}>
        Get notified of severe weather, evacuation orders, and nearby incidents.
        You can turn these off anytime in Settings.
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={requestPermission}
          style={{ flex: 1, padding: '9px', borderRadius: 9, background: T.amber, color: '#fff',
                   border: 'none', fontSize: 12, fontWeight: 800, cursor: 'pointer', fontFamily: T.font }}>
          Enable Alerts
        </button>
        <button onClick={onDismiss}
          style={{ padding: '9px 14px', borderRadius: 9, background: 'none',
                   border: `1px solid ${T.bdr}`, fontSize: 12, cursor: 'pointer',
                   color: T.dim, fontFamily: T.font }}>
          Not now
        </button>
      </div>
    </div>
  );
}

// ── Emergency modal (full-screen, requires interaction) ────────────
export function EmergencyAlertModal() {
  const { emergency, clearEmergency } = usePushNotifications();
  if (!emergency) return null;

  return (
    <BottomSheet open maxH="80vh" onClose={clearEmergency}>
      <div style={{ padding: '8px 16px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <span style={{ fontSize: 32 }}>🚨</span>
          <div>
            <div style={{ fontWeight: 900, fontSize: 18, color: '#dc2626' }}>
              {emergency.title}
            </div>
            <div style={{ fontSize: 11, color: T.dim }}>
              {emergency.alertType?.replace('_', ' ').toUpperCase()}
            </div>
          </div>
        </div>

        <div style={{ fontSize: 13, color: T.text, lineHeight: 1.6, marginBottom: 16 }}>
          {emergency.body}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {emergency.url && (
            <a href={emergency.url}
               style={{ display: 'block', padding: '12px', borderRadius: 10, background: T.blue,
                        color: '#fff', textAlign: 'center', textDecoration: 'none', fontSize: 14,
                        fontWeight: 800, fontFamily: T.font }}>
              View Details
            </a>
          )}
          <a href="tel:911"
             style={{ display: 'block', padding: '12px', borderRadius: 10, background: '#dc2626',
                      color: '#fff', textAlign: 'center', textDecoration: 'none', fontSize: 14,
                      fontWeight: 800, fontFamily: T.font }}>
            📞 Call 911
          </a>
          <button onClick={clearEmergency}
            style={{ padding: '10px', borderRadius: 10, border: `1px solid ${T.bdr}`,
                     background: 'none', fontSize: 13, cursor: 'pointer',
                     color: T.dim, fontFamily: T.font }}>
            Dismiss
          </button>
        </div>
      </div>
    </BottomSheet>
  );
}

// ── Foreground message banner ──────────────────────────────────────
export function ForegroundAlertBanner() {
  const { banner, clearBanner } = usePushNotifications();
  if (!banner) return null;

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
                  maxWidth: 430, margin: '0 auto',
                  background: '#1c1917', color: '#fff',
                  padding: '12px 14px', display: 'flex', alignItems: 'flex-start', gap: 10,
                  boxShadow: '0 4px 24px rgba(0,0,0,0.4)', borderBottom: '3px solid #d97706' }}>
      <span style={{ fontSize: 20, flexShrink: 0 }}>🔔</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 800, fontSize: 13 }}>{banner.title}</div>
        <div style={{ fontSize: 11, opacity: 0.8, marginTop: 2 }}>{banner.body}</div>
      </div>
      <button onClick={clearBanner}
        style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer',
                 fontSize: 18, padding: '0 4px', fontFamily: T.font }}>
        ×
      </button>
    </div>
  );
}
